import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import MeksaIndexPage from './pages/MeksaIndexPage';
import BrokerIndexTemplate from './pages/BrokerIndexTemplate';
import LoginPage from './pages/LoginPage';
import TradingPage from './pages/TradingPageNew';
import SimpleTradingDashboard from './pages/SimpleTradingDashboard';
import InvestmentDashboard from './pages/InvestmentDashboard';
import PositionsPage from './pages/PositionsPage';
import HeatMapPage from './pages/HeatMapPage';
import AnalyticsPage from './pages/AnalyticsPage';
import AlertsPage from './pages/AlertsPage';
import NewsPage from './pages/NewsPage';
import CalendarPage from './pages/CalendarPage';
import MultiChartPage from './pages/MultiChartPage';
import ScreenerPage from './pages/ScreenerPage';
import AdminPanelNew from './pages/AdminPanelNew';
import ManagerPanel from './pages/ManagerPanel';
import DepositPage from './pages/DepositPage';
import WithdrawPage from './pages/WithdrawPage';
import ProfilePage from './pages/ProfilePage';
import DocumentsPage from './pages/DocumentsPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import DuyurularPage from './pages/DuyurularPage';
import KVKKPage from './pages/KVKKPage';
import HakkimizdaPage from './pages/HakkimizdaPage';
import HesapBilgileriPage from './pages/HesapBilgileriPage';
import BlogPage from './pages/BlogPage';
import IletisimPage from './pages/IletisimPage';
import GenericPage from './pages/GenericPage';
import YatirimPage from './pages/YatirimPage';
import ViopPage from './pages/ViopPage';
import ForexPage from './pages/ForexPage';
import PortfoyPage from './pages/PortfoyPage';
import MeksanalizPage from './pages/MeksanalizPage';
import YurtdisiPage from './pages/YurtdisiPage';
import CommissionSettings from './pages/CommissionSettings';
import PriceMarginSettings from './pages/PriceMarginSettings';
import MarketTicker from './components/MarketTicker';
import LiveChatSupport from './components/LiveChatSupport';
import SupportPage from './pages/SupportPage';
import SupportTicketPage from './pages/SupportTicketPage';
import NotificationsPage from './pages/NotificationsPage';
import BackupPage from './pages/BackupPage';
import InstallPWA from './components/InstallPWA';
import NotificationToast from './components/NotificationToast';
import BottomNavigation from './components/BottomNavigation';
import { updatePWAManifest, registerServiceWorker } from './utils/pwaUtils';

// Runtime detection - works in both localhost and production
const API = (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'))
  ? 'http://localhost:8001/api' 
  : '/api';

// Global PWA install prompt - tüm bileşenler için
let globalDeferredPrompt = null;

// WebSocket URL for balance updates
const WS_URL = (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'))
  ? 'ws://localhost:8001'
  : `wss://${window.location.host}`;

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [homepageBroker, setHomepageBroker] = useState(null);
  const [brokerLoading, setBrokerLoading] = useState(true);
  const [pwaPrompt, setPwaPrompt] = useState(null);
  const wsBalanceRef = useRef(null);

  // WebSocket for real-time balance updates
  useEffect(() => {
    if (user?.id && !user?.is_admin) {
      console.log('🔌 App: Connecting Balance WebSocket for user:', user.id);
      
      // Close existing connection
      if (wsBalanceRef.current) {
        wsBalanceRef.current.close();
      }
      
      const ws = new WebSocket(`${WS_URL}/api/ws/balance/${user.id}`);
      
      ws.onopen = () => {
        console.log('💰 App: Balance WebSocket connected!');
      };
      
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'balance_update') {
            console.log('💰 App: Real-time balance update:', data.balance);
            setUser(prev => prev ? {...prev, balance: data.balance, credit: data.credit} : prev);
          }
        } catch (err) {
          console.error('Balance WS parse error:', err);
        }
      };
      
      ws.onerror = (error) => {
        console.error('💰 App: Balance WebSocket error');
      };
      
      ws.onclose = () => {
        console.log('💰 App: Balance WebSocket closed');
      };
      
      wsBalanceRef.current = ws;
      
      return () => {
        if (wsBalanceRef.current) {
          wsBalanceRef.current.close();
        }
      };
    }
  }, [user?.id]);

  useEffect(() => {
    // PWA beforeinstallprompt - GLOBAL olarak yakala
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      globalDeferredPrompt = e;
      window.deferredPrompt = e; // Window'a da kaydet
      setPwaPrompt(e);
      console.log('✅ PWA: beforeinstallprompt YAKALANDI (global)');
    };
    
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    
    // Sayfa yüklendiğinde zaten yakalanmış prompt var mı kontrol et
    if (window.deferredPrompt) {
      setPwaPrompt(window.deferredPrompt);
    }
    
    // Register service worker for PWA
    registerServiceWorker();

    // Update PWA manifest based on selected broker
    const brokerData = localStorage.getItem('selectedBroker');
    if (brokerData) {
      try {
        const broker = JSON.parse(brokerData);
        updatePWAManifest(broker.id);
      } catch (error) {
        console.error('Failed to parse broker data:', error);
      }
    }

    // Fetch homepage broker (if any)
    fetch(`${API}/homepage-broker`)
      .then(res => res.json())
      .then(data => {
        console.log('🏠 Homepage broker:', data);
        setHomepageBroker(data);
        setBrokerLoading(false);
      })
      .catch(err => {
        console.error('Failed to fetch homepage broker:', err);
        setBrokerLoading(false);
      });
  }, []);

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('token');
    console.log('🔍 Checking token:', token ? 'EXISTS' : 'NOT FOUND');
    
    if (token) {
      console.log('📡 Verifying token with server...');
      // Verify token
      fetch(`${API}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      .then(res => {
        console.log('📡 Server response status:', res.status);
        if (res.ok) {
          return res.json();
        } else if (res.status === 403) {
          // User is banned - force logout
          return res.json().then(data => {
            alert(data.detail || 'Hesabınız askıya alınmıştır.');
            throw new Error('User banned');
          });
        } else {
          throw new Error('Token invalid');
        }
      })
      .then(data => {
        console.log('✅ User data received:', data.email);
        setUser(data);
        setLoading(false);
      })
      .catch(err => {
        console.log('❌ Token verification failed:', err.message);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
        setLoading(false);
      });
    } else {
      console.log('❌ No token found');
      setLoading(false);
    }
  }, []);

  // Periodic auth check for ban status
  useEffect(() => {
    if (!user) return;
    
    const checkAuthStatus = () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      fetch(`${API}/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      .then(res => {
        if (res.status === 403) {
          // User banned - force logout
          return res.json().then(data => {
            alert(data.detail || 'Hesabınız askıya alınmıştır.');
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setUser(null);
            // Don't use hard redirect - let React Router handle it
          });
        }
        if (!res.ok) {
          throw new Error('Auth check failed');
        }
      })
      .catch(err => {
        console.log('Auth check error:', err);
      });
    };
    
    // Check every 30 seconds
    const interval = setInterval(checkAuthStatus, 30000);
    return () => clearInterval(interval);
  }, [user]);

  // Apply theme from localStorage
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'dark';
    document.documentElement.setAttribute('data-theme', savedTheme);
  }, []);

  // Global chat handler - triggered from anywhere (only opens live chat)
  useEffect(() => {
    const handleOpenChat = () => {
      setIsChatOpen(true);
    };
    window.openLiveChat = handleOpenChat;
    return () => delete window.openLiveChat;
  }, []);

  if (loading || brokerLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Yükleniyor...</div>
      </div>
    );
  }

  console.log('App render - User:', user, 'Loading:', loading, 'Homepage Broker:', homepageBroker);

  return (
    <>
      <BrowserRouter>
        {user && <MarketTicker />}
        {user && <NotificationToast />}
        
        <Routes>
          <Route path="/" element={
            user ? (
              user.is_admin ? <Navigate to="/admin" /> : 
              user.is_manager ? <Navigate to="/manager-panel" /> :
              <Navigate to="/dashboard" />
            ) : (
              <Navigate to="/login" />
            )
          } />
          <Route path="/login" element={!user ? <LoginPage setUser={setUser} broker={homepageBroker} /> : (
            user.is_admin ? <Navigate to="/admin" /> :
            user.is_manager ? <Navigate to="/manager-panel" /> :
            <Navigate to="/dashboard" />
          )} />
          <Route path="/dashboard" element={user ? <InvestmentDashboard user={user} setUser={setUser} /> : <Navigate to="/login" />} />
          <Route path="/trading" element={user ? <TradingPage user={user} setUser={setUser} /> : <Navigate to="/login" />} />
          <Route path="/positions" element={user ? <PositionsPage user={user} setUser={setUser} /> : <Navigate to="/login" />} />
          <Route path="/heatmap" element={user ? <HeatMapPage user={user} setUser={setUser} /> : <Navigate to="/login" />} />
          <Route path="/analytics" element={user ? <AnalyticsPage user={user} setUser={setUser} /> : <Navigate to="/login" />} />
          <Route path="/alerts" element={user ? <AlertsPage user={user} setUser={setUser} /> : <Navigate to="/login" />} />
          <Route path="/news" element={user ? <NewsPage user={user} setUser={setUser} /> : <Navigate to="/login" />} />
          {/* Takvim sayfası kaldırıldı */}
          <Route path="/multichart" element={user ? <MultiChartPage user={user} setUser={setUser} /> : <Navigate to="/login" />} />
          <Route path="/screener" element={user ? <ScreenerPage user={user} setUser={setUser} /> : <Navigate to="/login" />} />
          <Route path="/admin" element={user?.is_admin ? <AdminPanelNew user={user} setUser={setUser} /> : <Navigate to="/login" />} />
          <Route path="/admin/commission" element={user?.is_admin ? <CommissionSettings /> : <Navigate to="/login" />} />
          <Route path="/admin/price-margins" element={user?.is_admin ? <PriceMarginSettings /> : <Navigate to="/login" />} />
          <Route path="/manager-panel" element={user ? <ManagerPanel user={user} /> : <Navigate to="/login" />} />
          <Route path="/deposit" element={user ? <DepositPage user={user} setUser={setUser} /> : <Navigate to="/login" />} />
          <Route path="/withdraw" element={user ? <WithdrawPage user={user} setUser={setUser} /> : <Navigate to="/login" />} />
          <Route path="/profile" element={user ? <ProfilePage user={user} setUser={setUser} /> : <Navigate to="/login" />} />
          <Route path="/documents" element={user ? <DocumentsPage user={user} setUser={setUser} /> : <Navigate to="/login" />} />
          <Route path="/change-password" element={user ? <ChangePasswordPage user={user} setUser={setUser} /> : <Navigate to="/login" />} />
          <Route path="/support" element={user ? <SupportPage user={user} setUser={setUser} /> : <Navigate to="/login" />} />
          <Route path="/support-ticket" element={user ? <SupportTicketPage /> : <Navigate to="/login" />} />
          <Route path="/notifications" element={user ? <NotificationsPage user={user} setUser={setUser} /> : <Navigate to="/login" />} />
          <Route path="/backup" element={<BackupPage user={user} setUser={setUser} />} />
          
          {/* Public Pages */}
          <Route path="/duyurular" element={<DuyurularPage />} />
          <Route path="/kvkk" element={<KVKKPage />} />
          <Route path="/hakkimizda" element={<HakkimizdaPage />} />
          <Route path="/hesap-bilgileri" element={<HesapBilgileriPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/iletisim" element={<IletisimPage />} />
          <Route path="/sikayet-formu" element={<GenericPage title="Müşteri Şikayet Formu" content={<p className="text-gray-600">Şikayet formunuz en kısa sürede değerlendirilecektir.</p>} />} />
          <Route path="/bilgi-toplumu" element={<GenericPage title="Bilgi Toplumu Hizmetleri" content={<p className="text-gray-600">Bilgi toplumu hizmetleri hakkında detaylı bilgi.</p>} />} />
          <Route path="/zamanasimi" element={<GenericPage title="Zamanaşımı Duyuruları" content={<p className="text-gray-600">Zamanaşımı duyuruları için bu sayfayı takip edebilirsiniz.</p>} />} />
          <Route path="/masraf-ucret" element={<GenericPage title="Masraf Ücret ve Komisyonlar" content={<p className="text-gray-600">Güncel masraf, ücret ve komisyon tarifelerimiz.</p>} />} />
          
          {/* Menu Pages */}
          <Route path="/yatirim" element={<YatirimPage />} />
          <Route path="/viop" element={<ViopPage />} />
          <Route path="/forex" element={<ForexPage />} />
          <Route path="/portfoy" element={<PortfoyPage />} />
          <Route path="/meksanaliz" element={<MeksanalizPage />} />
          <Route path="/yurtdisi" element={<YurtdisiPage />} />
        </Routes>
        
        {/* Global Bottom Navigation - Tüm sayfalarda görünür */}
        {user && <BottomNavigation />}
      </BrowserRouter>

      {/* KÜÇÜK CHAT PENCERESİ - LiveChat/Tawk.to gibi */}
      {user && isChatOpen && (
        <div className="fixed inset-0 z-50 flex items-end justify-end p-4 pointer-events-none">
          <div 
            className="pointer-events-auto w-[400px] h-[600px] bg-gray-900 rounded-2xl shadow-2xl overflow-hidden flex flex-col"
            style={{ maxHeight: 'calc(100vh - 100px)' }}
          >
            {/* Chat Header */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center text-2xl">
                  💬
                </div>
                <div>
                  <div className="font-bold text-white">Canlı Destek</div>
                  <div className="text-xs text-white/80 flex items-center gap-1">
                    <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                    Online
                  </div>
                </div>
              </div>
              <button
                onClick={() => setIsChatOpen(false)}
                className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Chat Content */}
            <div className="flex-1 overflow-hidden">
              <LiveChatSupport 
                user={user}
                onClose={() => setIsChatOpen(false)}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default App;
